import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { type CbhFeatureFlags } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import {
  WorkerStages,
  WorkerSubstages,
  type WorkerSubstage,
} from "@src/appV2/redesign/utils/worker";

type BasketSizeLimitConfiguration =
  CbhFeatureFlags[typeof CbhFeatureFlag.BASKET_SIZE_LIMIT_CONFIGURATION];

interface ResolveWorkerStageUpdateArgs {
  /** The value picked in the status selector (a real or "virtual" stage). */
  stage: string;
  /** The worker's existing substage. */
  currentSubstage: WorkerSubstage | undefined;
  /** Basket-size-limit flag value; defaults to disabled. */
  basketConfig?: BasketSizeLimitConfiguration;
}

/**
 * Maps a status-selector choice to the `{ stage, substage }` payload for
 * PUT /agentProfile/stage. The selector value may be a real stage or a
 * "virtual" one that expands to a base stage + substage
 * (LIMITED/WARNING -> ENROLLED, INVITE_REQUIRED -> RESTRICTED).
 */
export function resolveWorkerStageUpdate({
  stage,
  currentSubstage,
  basketConfig,
}: ResolveWorkerStageUpdateArgs): { stage: string; substage: WorkerSubstage | undefined } {
  const { limited, warning } = basketConfig ?? {
    limited: { enabled: false },
    warning: { enabled: false },
  };

  let newStage = stage;
  let substage = currentSubstage;

  if (limited.enabled && stage === WorkerSubstages.LIMITED) {
    const { scoreThreshold, basketSize, leadTimeThresholdInHrs } = limited;
    newStage = WorkerStages.ENROLLED;
    substage = {
      name: WorkerSubstages.LIMITED,
      accessMetadata: {
        scoreThreshold,
        basketSize,
        leadTimeThresholdInHrs,
      },
    };
  }

  if (warning.enabled && stage === WorkerSubstages.WARNING) {
    const { scoreThreshold, basketSize, leadTimeThresholdInHrs, failRestrictionDurationInDays } =
      warning;
    newStage = WorkerStages.ENROLLED;
    substage = {
      name: WorkerSubstages.WARNING,
      accessMetadata: {
        scoreThreshold,
        basketSize,
        leadTimeThresholdInHrs,
        failRestrictionDurationInDays,
      },
    };
  }

  if (stage === WorkerSubstages.INVITE_REQUIRED) {
    newStage = WorkerStages.RESTRICTED;
    substage = { name: WorkerSubstages.INVITE_REQUIRED };
  } else if (stage === WorkerStages.ENROLLED || stage === WorkerStages.RESTRICTED) {
    substage = undefined;
  } else if (substage?.name === WorkerSubstages.INVITE_REQUIRED) {
    // INVITE_REQUIRED is only valid for RESTRICTED; drop it on other transitions.
    substage = undefined;
  }

  return { stage: newStage, substage };
}
